/**
 * useCueSync — design.md §5.5. The wash, the scheduled page turn, and both desync guards.
 *
 * ── The one idea, again ─────────────────────────────────────────────────────
 * **The sid is the only coordinate system.** Nothing here is addressed by page number, file
 * boundary, byte or column *first*: a cue names a sentence, the sentence resolves to an element, the
 * element's column is looked up, and only then does anything move. `columnOf(sid)` drives the turn —
 * **never the audio file boundary**, which is why a canonical page (~1500 chars ≈ 110 s of audio)
 * turning four reader columns on a phone and one on a MacBook needs no code and no configuration.
 *
 * ── Why this file is a class with an injected host and not just a hook ──────
 * Every assertion worth making here is about *timing*: an index that must lead the voice by 45 ms, a
 * turn that must be armed 140 ms early and must survive a 2× playback rate, a guard that must not
 * fire on a healthy page. None of that is observable through React, and this repo has no jsdom and
 * no React test renderer (`npm test` is bare `node --test`, and `package.json` is not mine to edit).
 * So the driver below takes a `CueSyncHost` — timers, frames, DOM lookups, the paginator — and
 * `test/cue-sync.test.ts` drives it with a fake clock. The hook at the bottom is the wiring, and it
 * is the only part that touches a real DOM.
 *
 * ── The three things that must never regress ────────────────────────────────
 * 1. **`requestAnimationFrame`, never `timeupdate`.** Safari fires `timeupdate` at ~4 Hz with
 *    jitter, so the highlight lands up to 250 ms late — which a user reads as "the app is broken",
 *    not "the timer is coarse".
 * 2. **The loop is ALWAYS rescheduled.** §4.2 puts 620–900 ms of `pageHead` silence at the head of
 *    every page, so `cueIndexAt` returns −1 for the first two-thirds of a second of *every* page.
 *    `cues[-1].sid` throws inside a rAF callback, the loop is never rescheduled, and the page plays
 *    with no highlight and no turn — recovering at the next page, so it reads as *flaky* rather than
 *    as a crash. The `i < 0` branch is handled and the body is wrapped in try/catch.
 * 3. **A cue whose sid is not in the DOM is DROPPED, with a visible reason, never snapped to the
 *    nearest span.** A read-along that highlights the wrong sentence and does not error makes the
 *    listener think they misread. That is the whole scar; it is the reason `cueSidsMatch` exists in
 *    `shared/narration/cue.ts` and the reason this file has a `dropped` counter on its public state.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  CUE_LEAD_MS,
  DESYNC_TOLERANCE_MS,
  TURN_LEAD_MS,
  cueIndexAt,
  cueSidsMatch,
  fracInCue,
  validatePageAudio,
  type Cue,
  type PageAudio,
} from "../../../shared/narration/cue.ts";
import type { Paginator } from "./Paginator.ts";

/* ══════════════════════════════════════════════════════════════════════════
 * The seams. Structural types, deliberately NOT imports.
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * The bit of a media element this file reads.
 *
 * ⚠️ **Deliberately structural, and deliberately not an `import type` of `AudioEngine`.** §5.7's
 * engine holds *two* `<audio>` elements and ping-pongs them, so "the clock" is whichever one is live
 * — a property the engine computes, not a module this file may reach into. Keeping the dependency
 * structural means the read-along typechecks and is testable before the engine exists, and an engine
 * that later declares `el: HTMLAudioElement` satisfies it with no adapter: `HTMLAudioElement` is
 * assignable to `MediaClock`.
 */
export interface MediaClock {
  /** Seconds. NaN until `loadedmetadata`; every read here is guarded (LAW 9 in spirit). */
  readonly currentTime: number;
  /** Seconds. The CONTAINER's duration, which is what desync guard 1 cross-checks. */
  readonly duration: number;
}

export interface NarrationClock {
  /** The element currently playing, or null between artifacts. */
  readonly el: MediaClock | null;
  /**
   * Must be React state upstream, not merely a field on the engine.
   *
   * The rAF loop stops itself when this is false — a 60 Hz loop on a paused reader in a pocket is a
   * battery bug, not a feature — and nothing inside this file can restart it, because a stopped loop
   * has no way to observe a mutation. The hook restarts on the prop changing. An engine that flips a
   * field without re-rendering leaves the read-along dead until the next page.
   */
  readonly playing: boolean;
  /**
   * 0.75–2.5 (§5.7, widened). Both leads are wall-clock quantities and are converted through it.
   *
   * ⚠️ **Named `playbackRate`, matching `HTMLMediaElement` and `AudioEngine`, and not `rate`.** It
   * is optional, so a clock spelling it `rate` would satisfy this interface, silently read
   * `undefined`, and fall back to 1 — and everything would look right until somebody set 1.5×, at
   * which point the wash leads by 30 ms instead of 45 and every scheduled turn lands a third of a
   * page late. There is no error and no screen that looks broken; it just stops feeling right. One
   * spelling, and it is the one the platform already uses.
   */
  readonly playbackRate?: number;
}

/** The two DOM calls this file makes, narrowed so a test can fake them with an object literal. */
export interface CueSyncElement {
  readonly classList: { add(token: string): void; remove(token: string): void };
}

export type TimerId = number;
export type FrameId = number;

/**
 * Everything the driver is not allowed to reach for itself: the DOM, the paginator, the clocks.
 *
 * One interface rather than five constructor arguments because the test fake is then a single object
 * whose every method is a recorded call — which is what makes "the turn was scheduled 140 ms early
 * at 1× and 70 ms early at 2×" an assertion instead of a screenshot.
 */
export interface CueSyncHost {
  clock(): NarrationClock | null;
  /**
   * §5.4: "Sentence-id → DOM is `track.querySelector('[data-s="…"]')` and nothing else. No Range
   * reconstruction from character offsets, ever, at runtime." Null when the sid is not mounted —
   * and a null here is a DROP, never a search for something close.
   */
  elementFor(sid: string): CueSyncElement | null;
  /** Every mounted sid in document order. Read once per page, by guard 1. */
  mountedSids(): readonly string[];
  /** The column of the sentence's FIRST client rect. See `noMidSentenceTurns` below. */
  columnOf(sid: string): number | undefined;
  /** The column showing now — the LEFT column in spread mode, already turn-aligned. */
  currentColumn(): number;
  /** Align a column to a turn boundary. Identity in single-column mode. */
  snapColumn(col: number): number;
  /** The ONLY thing this driver moves. Writes to the Paginator and to nothing else. */
  turnTo(column: number, opts: { animate: boolean }): void;
  /** LAW 12's flag, read at call time because it is owned by `usePageTurn`. */
  following(): boolean;
  now(): number;
  setTimer(fn: () => void, ms: number): TimerId;
  clearTimer(id: TimerId): void;
  requestFrame(fn: () => void): FrameId;
  cancelFrame(id: FrameId): void;
  emit(ev: CueSyncEvent): void;
  changed(state: CueSyncState): void;
  /**
   * "The voice is on a sentence this screen does not contain."
   *
   * Called on a dropped highlight, **before** the drop counter can disable the read-along. The driver
   * still drops — it never snaps to a nearby span, because a read-along that lights the wrong
   * sentence makes the listener think they misread — but only the component that owns navigation can
   * do the honest thing, which is to go to where the voice actually is.
   *
   * ⚠️ It is the reader's job to respect LAW 12 here: turning a page is browsing and never moves the
   * narration, but the narration moving the page IS the feature. The reader gates this on `following`.
   */
  offScreen?(sid: string): void;
}

/* ══════════════════════════════════════════════════════════════════════════
 * What this file reports, and what it exposes
 * ══════════════════════════════════════════════════════════════════════════ */

export type CueSyncEvent =
  /** The read-along was switched off for this page. `reason` is a code; `sentence` is the copy. */
  | { kind: "desync"; pageId: string; editionId: string; reason: string; detail: string }
  /** A cue named a sentence that is not on screen. Dropped, never snapped. */
  | { kind: "dropped-highlight"; pageId: string; editionId: string; sid: string }
  /**
   * §4.12: "logged automatically when a user scrubs back to a sentence within 3 s of it finishing.
   * It costs the user nothing, needs no UI, and is the highest-volume weak signal for *that sounded
   * wrong*. A sentence three people scrubbed back to is a bad sentence whether or not anyone opened
   * the menu."
   */
  | { kind: "replay-sentence"; pageId: string; editionId: string; sid: string; sinceEndMs: number }
  /** The rAF body threw. The loop is still running; this is the record that it did. */
  | { kind: "loop-error"; pageId: string; editionId: string; detail: string };

export interface CueSyncState {
  /** The sentence being spoken and pointed at, or null (head silence, paused, or a dropped cue). */
  spokenSid: string | null;
  /** The column the voice is on. Feeds LAW 12's pill and `usePageTurn.narrationColumn`. */
  narrationColumn: number | null;
  /** Non-null when the read-along is OFF. The audio keeps playing; this is the sentence we show. */
  disabled: string | null;
  /** Cues dropped on this page because their sentence was not on screen. */
  dropped: number;
  /** The one line the reader shows. Null when there is nothing honest to say. */
  notice: string | null;
}

const IDLE: CueSyncState = {
  spokenSid: null,
  narrationColumn: null,
  disabled: null,
  dropped: 0,
  notice: null,
};

export const NARRATING_CLASS = "narrating";

/**
 * A backwards jump into a sentence that finished this recently is a replay (§4.12).
 * Three seconds is the design's number and it is generous on purpose: the signal is meant to be
 * high-volume and weak, and a scrub that takes two seconds of thumb-dragging still counts.
 */
export const REPLAY_WINDOW_MS = 3_000;

/**
 * Drop this many highlights on one page and the cue list and the DOM are not describing the same
 * text. One drop is a `speak:false` edge or a part boundary; three is a wrong artifact, and
 * continuing to paint a sentence here and there out of a list that does not match is worse than
 * saying so.
 */
export const MAX_DROPPED_PER_PAGE = 3;

/**
 * Consecutive throws inside the rAF body before the read-along gives up.
 *
 * The loop is always rescheduled (that is rule 2 at the top of this file), so a transient throw —
 * one frame caught mid-relayout — costs nothing and the counter resets on the next good tick. But a
 * body that throws *every* frame is not a read-along, it is a 60 Hz error generator with a battery
 * attached, and the honest end state is "off, with a reason" rather than "on, and silent".
 */
export const MAX_LOOP_ERRORS = 8;

/* ══════════════════════════════════════════════════════════════════════════
 * The driver
 * ══════════════════════════════════════════════════════════════════════════ */

export class CueSync {
  private readonly host: CueSyncHost;

  private page: PageAudio | null = null;
  private painted: CueSyncElement | null = null;
  private index = -1;

  private rafId: FrameId | null = null;
  private turnTimer: TimerId | null = null;
  private running = false;

  private loopErrors = 0;
  private durationChecked = false;
  /** sid → wall ms at which we left it. Cleared per page; ~30 entries at most. */
  private readonly leftAt = new Map<string, number>();
  /** One report per reason per page: a guard that fires 60 times a second is a denial of service. */
  private readonly reported = new Set<string>();

  private state: CueSyncState = IDLE;

  constructor(host: CueSyncHost) {
    this.host = host;
  }

  /* ── lifecycle ─────────────────────────────────────────────────────────── */

  /**
   * Point the read-along at a page.
   *
   * `raw` is whatever came off the wire. **LAW 9: absent ≠ zero.** A missing `leadInMs` would make
   * `masterNow()` NaN, make `cueIndexAt` return a constant index, freeze the highlight for a whole
   * page — and, worse, make `NaN > DESYNC_TOLERANCE_MS` false, so the guard designed to catch
   * exactly this would silently switch itself off. `validatePageAudio` refuses instead, and the
   * refusal is visible.
   */
  setPage(raw: unknown): void {
    // `stop()` and not just `cancelTurn()`. A page that is refused (LAW 9) or absent leaves nothing
    // to compute, and a loop that keeps running against `this.page === null` is 60 wake-ups a second
    // producing nothing, with no symptom but a warm phone. The hook restarts it — its playing effect
    // depends on the page identity as well as on `playing`, precisely so that this can be honest.
    this.stop();
    this.clearHighlight();
    this.index = -1;
    this.loopErrors = 0;
    this.durationChecked = false;
    this.leftAt.clear();
    this.reported.clear();

    if (raw === null || raw === undefined) {
      this.page = null;
      this.publish({ ...IDLE });
      return;
    }

    const check = validatePageAudio(raw);
    if (!check.ok) {
      this.page = null;
      // We cannot name the page: the field that names it may be the missing one.
      this.host.emit({
        kind: "desync",
        pageId: pageIdOf(raw),
        editionId: editionIdOf(raw),
        reason: check.reason,
        detail: "the page's audio description was incomplete",
      });
      this.publish({
        ...IDLE,
        disabled: "The read-along is off for this page — its timing information didn't arrive intact.",
        notice: "The read-along is off for this page — its timing information didn't arrive intact.",
      });
      return;
    }

    this.page = check.page;
    this.publish({ ...IDLE });
    this.armOrderGuard();
  }

  /** Start the rAF loop. Idempotent. */
  start(): void {
    if (this.running || !this.page || this.state.disabled) return;
    this.running = true;
    this.scheduleFrame();
  }

  /** Stop the loop. The highlight STAYS where it is: a pause does not un-say the sentence. */
  stop(): void {
    this.running = false;
    if (this.rafId !== null) {
      this.host.cancelFrame(this.rafId);
      this.rafId = null;
    }
    this.cancelTurn();
  }

  dispose(): void {
    this.stop();
    this.clearHighlight();
    this.page = null;
  }

  /** For the hook and for tests. */
  get snapshot(): CueSyncState {
    return this.state;
  }

  /**
   * §5.7: "On `visible`, **one synchronous resync before paint**: recompute the cue index from
   * `currentTime`, jump the highlight, jump the page with `animate:false`, restart the rAF. **No
   * catching-up animation.**"
   *
   * Also called after every relayout, because a font-size change rebuilds `columnOf` from scratch
   * and every column this driver is holding — the one it turned to, the one it has a timer armed for
   * — describes a layout that no longer exists. This is the other half of the acceptance test: the
   * audio does not seek or gap *and* the highlight is still on the spoken sentence, on the right
   * page, with no slide.
   */
  resync(): void {
    if (!this.page || this.state.disabled) return;
    this.cancelTurn();

    const m = this.masterNow();
    if (!Number.isFinite(m)) return;

    const i = cueIndexAt(this.page.cues, m + this.leadCueMs());
    if (i < 0) {
      this.index = -1;
      this.clearHighlight();
      this.publish({ ...this.state, spokenSid: null, narrationColumn: null });
    } else {
      // `animate: false` — this is the jump, not a turn. A 220 ms slide across nine columns of a
      // book the reader cannot read yet is the catching-up animation the design refuses.
      this.index = i;
      this.applyIndex(i, false);
    }

    if (this.running && this.rafId === null) this.scheduleFrame();
  }

  /* ── the loop ──────────────────────────────────────────────────────────── */

  private scheduleFrame(): void {
    this.rafId = this.host.requestFrame(this.onFrame);
  }

  private readonly onFrame = (): void => {
    this.rafId = null;
    if (!this.running) return;

    // ⚠️ **Every call to the host is inside the try, including reading the clock.** The obvious
    // shape puts `if (!clock.playing) return` above it — and then a throw from `clock()` escapes
    // into the rAF callback, where nothing catches it, the loop is never rescheduled, and the page
    // plays with no highlight and no turn until the next artifact. That is the exact failure mode
    // this file's rule 2 exists to prevent, reintroduced one line above the guard against it.
    let paused = false;
    try {
      const clock = this.host.clock();
      if (!clock || !clock.playing) {
        paused = true;
      } else {
        this.tick(clock);
        this.loopErrors = 0;
      }
    } catch (err) {
      this.loopErrors++;
      const detail = err instanceof Error ? err.message : String(err);
      this.report("loop-error", () => ({
        kind: "loop-error",
        pageId: this.page?.pageId ?? "",
        editionId: this.page?.editionId ?? "",
        detail,
      }));
      if (this.loopErrors >= MAX_LOOP_ERRORS) {
        this.disable("The read-along stopped keeping up with the audio. The book is still playing.");
        return;
      }
    }

    // A paused engine stops the loop; nothing here can restart it, and that is stated on
    // `NarrationClock.playing`. The highlight STAYS where it is — a pause does not un-say a
    // sentence — and any armed turn is cancelled, because a page turning under a reader who
    // pressed pause is the narration moving somebody, which is LAW 12 read backwards.
    if (paused) {
      this.running = false;
      this.cancelTurn();
      return;
    }

    // `tick` can end the read-along from the inside — the third dropped highlight, or the duration
    // guard resolving against a DOM that has just changed — and `disable()` calls `stop()`, which
    // cancels a frame id that `onFrame` already set to null on entry. Without this line the loop
    // arms one more frame after it has been switched off: harmless today because `running` is false
    // and the next frame returns immediately, and exactly the kind of leak that becomes a live
    // handle the moment someone gives `disable()` a recovery path. Caught by the test that asserts
    // no rAF is pending after MAX_DROPPED_PER_PAGE.
    if (!this.running) return;

    // ⚠️ ALWAYS rescheduled. This line is the whole of rule 2.
    this.scheduleFrame();
  };

  private tick(clock: NarrationClock): void {
    const page = this.page;
    if (!page) return;

    const m = this.masterNow();
    if (!Number.isFinite(m)) return; // no metadata yet; not an error, and not a desync

    this.checkDurationOnce(clock);
    if (this.state.disabled) return;

    if (page.cues.length === 0) return; // a page of captions and tables. Nothing to point at.

    const i = cueIndexAt(page.cues, m + this.leadCueMs());

    if (i < 0) {
      // §4.2's `pageHead` silence, at the head of EVERY page. Not defensive padding.
      if (this.index !== -1) {
        this.index = -1;
        this.clearHighlight();
        this.publish({ ...this.state, spokenSid: null, narrationColumn: null });
      }
      return;
    }

    if (i === this.index) return;

    const prev = this.index;
    this.index = i;
    if (prev >= 0) {
      const left = page.cues[prev];
      if (left) this.leftAt.set(left.sid, this.host.now());
    }
    this.noteReplay(i);
    this.applyIndex(i, true);
  }

  /* ── the highlight ─────────────────────────────────────────────────────── */

  private applyIndex(i: number, animate: boolean): void {
    const page = this.page;
    const cue = page?.cues[i];
    if (!page || !cue) return;

    const el = this.host.elementFor(cue.sid);
    if (!el) {
      // **Dropped. Never snapped to the nearest span.** The audio is right and the map is wrong;
      // pointing at a sentence we merely believe is close is how a listener concludes they misread.
      this.clearHighlight();
      const dropped = this.state.dropped + 1;
      this.report(`dropped:${cue.sid}`, () => ({
        kind: "dropped-highlight",
        pageId: page.pageId,
        editionId: page.editionId,
        sid: cue.sid,
      }));
      /**
       * ⚠️ **The commonest reason a cue does not resolve is that the voice has walked off this
       * screen, and until now nothing acted on it.** A canonical page never crosses a chapter, but
       * the audio does — `pagesAfter` walks the whole book — and one chapter is mounted at a time,
       * split further into `chapters.parts`. So at every chapter boundary (and every part boundary
       * inside a long chapter) three cues in a row named unmounted sentences, the counter hit
       * `MAX_DROPPED_PER_PAGE`, and the read-along switched itself off with a notice blaming the
       * artifact — while the audio was perfectly correct and the screen was frozen on the last column
       * of the previous chapter, for the rest of the book. That is P2's gate ("the page turns itself",
       * "four page boundaries") failing in the one place a seven-page chapter hides it.
       */
      this.host.offScreen?.(cue.sid);
      if (dropped >= MAX_DROPPED_PER_PAGE) {
        this.publish({ ...this.state, dropped, spokenSid: null, narrationColumn: null });
        this.disable(
          "The read-along is off for this page — the audio names sentences that aren't on this screen.",
        );
        return;
      }
      this.publish({
        ...this.state,
        dropped,
        spokenSid: null,
        narrationColumn: null,
        notice: "One sentence couldn't be lit — the audio is still right.",
      });
      this.scheduleTurn(i);
      return;
    }

    this.paint(el);
    const col = this.host.columnOf(cue.sid);
    const column = col === undefined ? null : col;
    // ⚠️ `notice: null`, explicitly. Every `publish` spreads the previous state, so the one-drop
    // notice was sticky for the rest of the page: one legitimate `speak:false` edge or part-straddle
    // put "One sentence couldn't be lit" under the playback bar and left it there for the next two
    // minutes while everything worked. A warning that no longer describes anything teaches the
    // listener to ignore the one that will.
    this.publish({ ...this.state, spokenSid: cue.sid, narrationColumn: column, notice: null });
    if (column !== null) this.turnToColumn(column, animate);
    this.scheduleTurn(i);
  }

  private paint(el: CueSyncElement): void {
    if (this.painted === el) return;
    this.painted?.classList.remove(NARRATING_CLASS);
    // `.narrating` is books' rule lifted verbatim, and `box-decoration-break: clone` in it is
    // load-bearing: without it a wrapped sentence gets one rounded box spanning the column instead
    // of a stroke per line — and it does the right thing across a COLUMN boundary too, which is the
    // case this reader has and a scrolling reader does not. No dimming of surrounding text: every
    // read-along product does it and it makes reading ahead physically unpleasant, which this
    // design explicitly supports.
    el.classList.add(NARRATING_CLASS);
    this.painted = el;
  }

  private clearHighlight(): void {
    this.painted?.classList.remove(NARRATING_CLASS);
    this.painted = null;
  }

  /* ── the turn ──────────────────────────────────────────────────────────── */

  /**
   * §5.5: "The turn is driven by `columnOf(sid)`, **never by the audio file boundary**, and because
   * we know each cue's start in advance it is *scheduled*, not reacted to."
   *
   * **No mid-sentence turns, ever.** A sentence straddling a column turns at its *first* client rect
   * and stays — which falls out of two facts held together and is stated here so nobody re-adds a
   * second turn as a "polish" commit: `Paginator.measure()` maps a sid to the column of `rects[0]`,
   * and this scheduler only ever fires at a *cue* boundary. There is no code path that can turn in
   * the middle of a spoken sentence, and there must never be one: a turn 55% of the way through a
   * sentence is jarring in a way a turn between sentences is not.
   */
  private scheduleTurn(fromIndex: number): void {
    this.cancelTurn();
    const page = this.page;
    if (!page) return;
    const here = page.cues[fromIndex];
    if (!here) return;
    const hereCol = this.host.columnOf(here.sid);
    if (hereCol === undefined) return;
    const hereTurn = this.host.snapColumn(hereCol);

    for (let j = fromIndex + 1; j < page.cues.length; j++) {
      const next = page.cues[j];
      if (!next) continue;
      const col = this.host.columnOf(next.sid);
      if (col === undefined) continue; // not mounted: it cannot be turned to, so it decides nothing
      if (this.host.snapColumn(col) === hereTurn) continue;

      const delay = this.turnDelayMs(next);
      if (!Number.isFinite(delay)) return;
      this.turnTimer = this.host.setTimer(() => {
        this.turnTimer = null;
        this.turnToColumn(col, true);
      }, Math.max(0, delay));
      return;
    }
  }

  /**
   * `setTimeout(turn, (cue.start - masterNow()) - TURN_LEAD_MS)` — with the one correction §5.5 does
   * not make, because the design's formula is written for `playbackRate === 1`.
   *
   * ⚠️ **A timer is wall clock; a cue is audio clock.** `cue.start - masterNow()` is an interval in
   * *audio* milliseconds, and at rate 2.0 the audio crosses it in half the wall time. Scheduling the
   * raw interval at 2× lands every turn roughly half a page late — the eye arrives after the voice,
   * on exactly the setting a re-listener uses. So the interval is divided by the rate and the
   * 140 ms lead, which is an animation lead and therefore already wall clock, is subtracted after.
   * At rate 1 this is character-for-character §5.5's expression.
   */
  private turnDelayMs(next: Cue): number {
    const rate = this.rate();
    return (next.start - this.masterNow()) / rate - TURN_LEAD_MS;
  }

  private turnToColumn(column: number, animate: boolean): void {
    // ⚠️ **LAW 12. Turning a page is browsing; it never moves the narration — and the converse is
    // the half this line enforces: the narration never moves a reader who has walked away from it.**
    // `following` is false the moment a human turns a page, and the pill is what offers the way
    // back. Turning anyway is how you cannot flip back to check a name without being dragged
    // forward, which §5.5 calls the single most common thing a listener does.
    if (!this.host.following()) return;
    if (this.host.snapColumn(column) === this.host.snapColumn(this.host.currentColumn())) return;
    this.host.turnTo(column, { animate });
  }

  private cancelTurn(): void {
    if (this.turnTimer !== null) {
      this.host.clearTimer(this.turnTimer);
      this.turnTimer = null;
    }
  }

  /* ── the guards ────────────────────────────────────────────────────────── */

  /**
   * Guard 1, half one: does this cue list describe the text on screen?
   *
   * Run at page arm and again on every resync, because a relayout remounts nothing but a *part*
   * change remounts everything. `cueSidsMatch` is the shared predicate the stitch calls before it
   * writes an artifact; calling the same function here is the point of it living in `shared/`.
   *
   * ⚠️ It is only conclusive when **every** cue resolves. A canonical page may legitimately straddle
   * a `chapters.parts` boundary (§5.1 splits at 60k chars on a paragraph, not on a page), so cues
   * naming sentences in the *unmounted* part are expected and are guard 2's business. What is never
   * legitimate is a cue list whose order or multiplicity disagrees with text that is entirely
   * present — that is a wrong artifact, and it highlights backwards without erroring.
   */
  private armOrderGuard(): void {
    const page = this.page;
    if (!page || page.cues.length === 0) return;

    const sids = this.host.mountedSids();
    if (sids.length === 0) return; // nothing mounted yet; resync will run this again
    const present = new Set(sids);
    for (const cue of page.cues) if (!present.has(cue.sid)) return;

    const why = cueSidsMatch(page.cues, sids);
    if (why) {
      this.host.emit({
        kind: "desync",
        pageId: page.pageId,
        editionId: page.editionId,
        reason: `cue-order:${why}`,
        detail: why,
      });
      this.disable("The read-along is off for this page — its sentences are out of order.");
    }
  }

  /**
   * Guard 1, half two: the duration cross-check, at **150 ms and not 60**.
   *
   * An AAC frame is 1024 samples = 42.67 ms at 24 kHz, so a container's duration is quantised by
   * more than 60 ms even when everything is right; a 60 ms tolerance fires on healthy pages, and a
   * guard that cries wolf gets deleted by the third person who sees it.
   *
   * ⚠️ **A duration mismatch ALONE never disables the read-along.** The guard additionally requires
   * that the first cue's sid fails to resolve in the DOM. One of those two facts is about encoding
   * and is frequently a rounding artefact; the two together mean the cue list and the text are from
   * different editions, which is the only condition that justifies taking the feature away.
   *
   * Runs once, the first time the container reports a duration — `el.duration` is NaN until
   * `loadedmetadata`, and a check that ran eagerly would compare against NaN and pass silently.
   */
  private checkDurationOnce(clock: NarrationClock): void {
    if (this.durationChecked) return;
    const page = this.page;
    const el = clock.el;
    if (!page || !el) return;
    const containerMs = el.duration * 1000;
    if (!Number.isFinite(containerMs) || containerMs <= 0) return;
    this.durationChecked = true;

    const drift = Math.abs(containerMs - page.durationMs);
    if (drift <= DESYNC_TOLERANCE_MS) return;

    const firstCue = page.cues[0];
    const firstResolves = firstCue ? this.host.elementFor(firstCue.sid) !== null : true;

    this.host.emit({
      kind: "desync",
      pageId: page.pageId,
      editionId: page.editionId,
      reason: firstResolves ? "duration-drift" : "duration-drift+first-cue-missing",
      detail: `container ${Math.round(containerMs)} ms vs measured ${Math.round(page.durationMs)} ms`,
    });

    if (!firstResolves) {
      this.disable("The read-along is off for this page — this audio was made for a different copy of it.");
    }
  }

  private disable(sentence: string): void {
    this.stop();
    this.clearHighlight();
    this.index = -1;
    this.publish({ ...this.state, spokenSid: null, narrationColumn: null, disabled: sentence, notice: sentence });
  }

  /* ── telemetry ─────────────────────────────────────────────────────────── */

  /** §4.12's free signal: a backwards jump into a sentence that finished within 3 s is a replay. */
  private noteReplay(i: number): void {
    const page = this.page;
    const cue = page?.cues[i];
    if (!page || !cue) return;
    const left = this.leftAt.get(cue.sid);
    if (left === undefined) return;
    const since = this.host.now() - left;
    this.leftAt.delete(cue.sid);
    if (since > REPLAY_WINDOW_MS) return;
    this.host.emit({
      kind: "replay-sentence",
      pageId: page.pageId,
      editionId: page.editionId,
      sid: cue.sid,
      sinceEndMs: Math.round(since),
    });
  }

  /** One emission per (page, key). A guard that reports every frame is a self-inflicted outage. */
  private report(key: string, build: () => CueSyncEvent): void {
    if (this.reported.has(key)) return;
    this.reported.add(key);
    this.host.emit(build());
  }

  /* ── clocks ────────────────────────────────────────────────────────────── */

  /**
   * The master clock: milliseconds into the page's own timeline.
   *
   * `leadInMs` is subtracted here and added back in the desync guard. It is measured at stitch and
   * is currently always 0 for AAC/MP4 — and it is emitted anyway, because a field that is absent
   * rather than zero is the bug LAW 9 exists to prevent, and `validatePageAudio` has already refused
   * a page that omitted it before this line can run.
   */
  private masterNow(): number {
    const el = this.host.clock()?.el;
    const page = this.page;
    if (!el || !page) return NaN;
    const t = el.currentTime;
    if (!Number.isFinite(t)) return NaN;
    return t * 1000 - page.leadInMs;
  }

  private rate(): number {
    const r = this.host.clock()?.playbackRate;
    return typeof r === "number" && Number.isFinite(r) && r > 0 ? r : 1;
  }

  /**
   * The wash must LEAD the voice by 45 ms of *wall* time; lagging reads as broken.
   *
   * Which is `CUE_LEAD_MS * rate` of audio time — at 2× the voice covers 90 ms of the artifact in
   * the 45 ms the eye needs. At rate 1 this is exactly §5.5's `+ CUE_LEAD_MS`.
   */
  private leadCueMs(): number {
    return CUE_LEAD_MS * this.rate();
  }

  private publish(next: CueSyncState): void {
    if (
      next.spokenSid === this.state.spokenSid &&
      next.narrationColumn === this.state.narrationColumn &&
      next.disabled === this.state.disabled &&
      next.dropped === this.state.dropped &&
      next.notice === this.state.notice
    ) {
      return;
    }
    this.state = next;
    this.host.changed(next);
  }
}

/* ══════════════════════════════════════════════════════════════════════════
 * Seeking — LAW 3, and the only thing in this file that moves the audio
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Resolve a millisecond target inside a page to the sentence it lands in.
 *
 * **LAW 3: `ms` is advisory; resolve in-page offsets from your OWN cue list by `sid` + `frac`.** The
 * scrubber and the co-read timeline both speak in milliseconds, and both of them are describing an
 * artifact that may not be byte-identical to the one in this tab. Handing the raw number to
 * `el.currentTime` is how a client seeks confidently and wrongly; handing back a `{sid, frac}` makes
 * the same gesture correct against whichever artifact is actually loaded.
 *
 * Null before the first cue — the `pageHead` silence is not a sentence, and a seek into it should
 * land on the page's first sentence, which is the caller's decision to make explicitly.
 */
export function seekTargetInPage(page: PageAudio, ms: number): { sid: string; frac: number } | null {
  const i = cueIndexAt(page.cues, ms - page.leadInMs);
  const cue = i < 0 ? undefined : page.cues[i];
  if (!cue) return null;
  return { sid: cue.sid, frac: fracInCue(cue, ms - page.leadInMs) };
}

/* ══════════════════════════════════════════════════════════════════════════
 * One-shot frames over a persistent tick
 * ══════════════════════════════════════════════════════════════════════════ */

export interface OneShotFrames {
  /** Ask for the next tick. A second call before the tick REPLACES the first. */
  request(fn: () => void): void;
  cancel(): void;
  /** Drop the subscription. Idempotent. */
  close(): void;
}

/**
 * Adapt a persistent tick — `AudioEngine.subscribe` — to the one-shot `requestAnimationFrame` shape
 * the driver expects.
 *
 * The driver asks for one frame and re-asks from *inside* its own callback; that re-ask is rule 2,
 * "ALWAYS rescheduled". So the callback must be pulled out of the slot and the slot cleared
 * **before** it runs, or the re-ask is immediately overwritten by the clear that follows it and the
 * loop stops after exactly one frame — a read-along that highlights the first sentence of every page
 * and then freezes, with nothing in the console.
 *
 * A slot and not a queue: two pending callbacks would mean the driver had scheduled twice, which it
 * cannot, and running both would swap every highlight twice per frame. Extracted from the hook and
 * exported so `test/cue-sync.test.ts` can drive exactly that re-entrancy, which is otherwise only
 * reachable through a rendered component and a real engine.
 */
export function oneShotFrames(subscribe: (tick: () => void) => () => void): OneShotFrames {
  let pending: (() => void) | null = null;
  let off: (() => void) | null = null;

  return {
    request(fn) {
      pending = fn;
      off ??= subscribe(() => {
        const next = pending;
        pending = null; // cleared BEFORE the call, so the call may re-arm it
        next?.();
      });
    },
    cancel() {
      pending = null;
    },
    close() {
      pending = null;
      off?.();
      off = null;
    },
  };
}

/* ══════════════════════════════════════════════════════════════════════════
 * The hook
 * ══════════════════════════════════════════════════════════════════════════ */

export interface UseCueSyncOptions {
  /**
   * The page's audio description, straight off the wire and NOT pre-validated — validation is LAW
   * 9's and it belongs where the refusal can be shown. Pass a stable object: identity is what tells
   * this hook a new page has arrived.
   */
  page: unknown;
  clock: NarrationClock | null;
  /** Must be React state. See `NarrationClock.playing`. */
  playing: boolean;
  /** The mounted chapter part. Every sid lookup is scoped to it. */
  track: HTMLElement | null;
  paginator: Paginator | null;
  /**
   * LAW 12's flag, as a **ref** and not a value.
   *
   * `usePageTurn` owns `following` and needs `narrationColumn`, which this hook produces — a value
   * prop would be a cycle. A ref written during render (the pattern this reader already uses for
   * `live`) breaks it with no effects and no stale closure, because the driver reads it at call
   * time, inside a timer that may fire long after the render that armed it.
   */
  following: { readonly current: boolean };
  onEvent?: ((ev: CueSyncEvent) => void) | null;
  /**
   * Where frames come from. Subscribe, and return the unsubscribe.
   *
   * ⚠️ **Pass the audio engine's tick, not `requestAnimationFrame`, whenever there is an engine.**
   * `AudioEngine` already runs one rAF loop for the whole player — it has to, because it carries
   * §5.7's end-of-file fallback (`currentTime >= duration - 0.04`, because Safari does not reliably
   * fire `ended` for short blob-backed media) — and it emits a `tick` from inside it for exactly this
   * subscriber. Two loops is two wakeups per frame for one clock, on a phone, with the screen off,
   * for hours: the thing P2's gate is *specifically* about. This hook falls back to
   * `requestAnimationFrame` when nothing is passed, so it still works standing alone and in a test.
   *
   * The engine calls its listeners inside a `try` and this driver catches its own throws, so a
   * failure on either side of the seam cannot strand the other's loop.
   */
  frames?: ((tick: () => void) => () => void) | null;
  /**
   * "The voice is on a sentence this screen does not contain — take me there."
   *
   * The reader wires this to its own navigation and gates it on `following` (LAW 12). Omitting it
   * keeps P1's behaviour exactly: the cue is dropped, three drops disable the read-along, and the
   * page stops turning at the chapter boundary.
   */
  onOffScreen?: ((sid: string) => void) | null;
}

export interface CueSyncApi extends CueSyncState {
  /** §5.7's synchronous resync. Call after a relayout and on `visibilitychange → visible`. */
  resync(): void;
}

export function useCueSync(opts: UseCueSyncOptions): CueSyncApi {
  const live = useRef(opts);
  live.current = opts;

  const [state, setState] = useState<CueSyncState>(IDLE);

  /**
   * One-shot frame semantics over whatever frame source we were given.
   *
   * The driver asks for one frame at a time and re-asks from inside the callback (that re-ask is
   * rule 2, "ALWAYS rescheduled"). An engine tick is a *persistent* subscription, so the adapter
   * holds a single pending callback: the tick pulls it, clears it, and calls it — and the call sets
   * it again before returning. A slot rather than a queue, because two pending callbacks would mean
   * the driver had scheduled twice, which it cannot, and silently running both would double every
   * highlight swap. The subscription itself is opened once and closed on unmount; while nothing is
   * pending the listener does nothing, which is cheaper than resubscribing sixty times a second.
   */
  const frames = useRef<OneShotFrames | null>(null);

  // The host is built once and reads everything through `live`. Rebuilding it would re-create the
  // driver, which would drop the highlight and every armed timer on a prop change that changed
  // nothing — and props change here on every rendered frame of the playback bar.
  const host = useMemo<CueSyncHost>(
    () => ({
      clock: () => live.current.clock,
      elementFor(sid) {
        const track = live.current.track;
        if (!track) return null;
        // §5.4, exactly: one attribute selector. `CSS.escape` because a sid is `c003.b0042.s1` and
        // the dots are attribute-value content, not selector syntax — they are safe inside quotes,
        // but escaping is what keeps this correct if a sid ever grows a character that is not.
        return track.querySelector<HTMLElement>(`[data-s="${cssEscape(sid)}"]`);
      },
      mountedSids() {
        const track = live.current.track;
        if (!track) return [];
        const out: string[] = [];
        for (const el of track.querySelectorAll<HTMLElement>("[data-s]")) {
          const sid = el.getAttribute("data-s");
          if (sid) out.push(sid);
        }
        return out;
      },
      columnOf: (sid) => live.current.paginator?.columnForSid(sid),
      currentColumn: () => live.current.paginator?.page ?? 0,
      snapColumn: (col) => live.current.paginator?.snap(col) ?? col,
      turnTo(column, o) {
        live.current.paginator?.goTo(column, { animate: o.animate });
      },
      following: () => live.current.following.current,
      now: () => (typeof performance !== "undefined" ? performance.now() : Date.now()),
      setTimer: (fn, ms) => setTimeout(fn, ms) as unknown as TimerId,
      clearTimer: (id) => clearTimeout(id),
      requestFrame(fn) {
        const source = live.current.frames;
        if (!source) return requestAnimationFrame(fn);
        (frames.current ??= oneShotFrames(source)).request(fn);
        return -1; // not a rAF handle; `cancelFrame` never treats it as one
      },
      cancelFrame(id) {
        if (id === -1) frames.current?.cancel();
        else cancelAnimationFrame(id);
      },
      emit(ev) {
        live.current.onEvent?.(ev);
        beaconSync(ev);
      },
      offScreen(sid) {
        live.current.onOffScreen?.(sid);
      },
      changed: setState,
    }),
    [],
  );

  const driverRef = useRef<CueSync | null>(null);
  if (driverRef.current === null) driverRef.current = new CueSync(host);
  const driver = driverRef.current;

  useEffect(
    () => () => {
      driver.dispose();
      frames.current?.close();
      frames.current = null;
    },
    [driver],
  );

  useEffect(() => {
    driver.setPage(opts.page);
  }, [driver, opts.page]);

  useEffect(() => {
    if (opts.playing) driver.start();
    else driver.stop();
  }, [driver, opts.playing, opts.page]);

  // A part change replaces every element in the track, so every sid this driver is holding now
  // resolves to a detached node or to nothing. Re-arm rather than wait for the next cue.
  useEffect(() => {
    driver.resync();
  }, [driver, opts.track, opts.paginator]);

  const resync = useCallback(() => driver.resync(), [driver]);
  return useMemo(() => ({ ...state, resync }), [state, resync]);
}

/* ══════════════════════════════════════════════════════════════════════════
 * The beacon
 * ══════════════════════════════════════════════════════════════════════════ */

const SYNC_TELEMETRY_URL = "/api/v1/telemetry/sync";
let warnedMissingRoute = false;

/**
 * §10's `POST /api/v1/telemetry/sync`.
 *
 * ⚠️ **This route is not implemented on the server as of P2, and this function says so out loud —
 * once — rather than swallowing the 404.** P1 shipped `beaconLayout` swallowing every failure "so
 * telemetry never surfaces to the reader", and the consequence is recorded in `repaginate.ts`: the
 * whole of §13.1's instrumentation fired into a wall with nothing in the console, nothing on screen
 * and an empty table, and that was the *shipped* state until somebody counted the 404s in a browser.
 * A beacon nobody reads is not worth collecting, so the failure gets exactly one `console.warn` —
 * enough for the first person to open devtools, quiet enough that it can never become the reader's
 * problem.
 *
 * The user-visible half never depends on this: every desync also raises a sentence on screen.
 */
function beaconSync(ev: CueSyncEvent): void {
  try {
    void fetch(SYNC_TELEMETRY_URL, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(ev),
      keepalive: true,
      credentials: "same-origin",
    })
      .then((res) => {
        if (res.ok || warnedMissingRoute) return;
        warnedMissingRoute = true;
        console.warn(
          `[cue-sync] ${SYNC_TELEMETRY_URL} answered ${res.status}; read-along telemetry is going nowhere.`,
        );
      })
      .catch(() => {
        // Offline. Not a finding, and never the reader's problem.
      });
  } catch {
    /* fetch can throw synchronously on a torn-down document. */
  }
}

/* ══════════════════════════════════════════════════════════════════════════
 * Small things
 * ══════════════════════════════════════════════════════════════════════════ */

function cssEscape(v: string): string {
  return typeof CSS !== "undefined" && typeof CSS.escape === "function"
    ? CSS.escape(v)
    : v.replace(/["\\]/g, "\\$&");
}

/** Best effort, for the one report we make about a page whose own id may be the missing field. */
function pageIdOf(raw: unknown): string {
  const v = (raw as { pageId?: unknown } | null)?.pageId;
  return typeof v === "string" ? v : "";
}

function editionIdOf(raw: unknown): string {
  const v = (raw as { editionId?: unknown } | null)?.editionId;
  return typeof v === "string" ? v : "";
}
